import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, FileSignature, Search, Users, Wallet } from "lucide-react";
import { apiUrl } from "@/lib/api";
import { trackEvent, getAnonymousId } from "@/lib/tracker";
import { SiftMark } from "@/components/SiftWordmark";
import StepProgress from "@/components/StepProgress";
import CompanyNeedComposer from "./CompanyNeedComposer";
import GetStartedCompany from "./GetStartedCompany";
import GetStartedNeedFirst from "./GetStartedNeedFirst";

/* The company side's first screen, in both arms of company_signup_flow:
   "What can we help you with?" over an empty box (Jake, 2026-10-04).

   It replaced CompanyDoors. The doors asked people to pick a category before
   they had said anything, and in September 31 of the 46 who picked one chose
   "I'm looking for work". Now they say it in their own words, the server
   reads it (onboarding/signup_ask.py, saved for review in the admin), and
   they go where it fits:

     hire, one_off   draft the post, then the arm's own last two screens
                     (current: CompanyNeedComposer's preview, GetStartedCompany;
                      new: GetStartedNeedFirst's 1 of 2 and 2 of 2)
     manage, info    a short answer to what they asked, then the account
     find_clients    asked back what kind of clients and who does the work:
                     a company gets the short answer, the account, then its
                     storefront; one person goes to the individual side
     find_work       straight to the individual side, their words carried over

   A place on its own ("Carmel") is asked back: looking for work there, or
   hiring there? Anything else the rules can't read gets a row of choices
   instead of a guess.
   Text typed into the landing page's search box arrives here and is read
   without asking again. */

const CHOICES = [
  { key: "hire", label: "Hire someone", hint: "Post what you need and see who fits" },
  { key: "find_clients", label: "Find clients for my work", hint: "Get found and get requests for what you do" },
  { key: "manage", label: "Manage people I already work with", hint: "Contracts, timecards and pay in one place" },
  { key: "one_off", label: "Get a one-off job done", hint: "A repair, an install, a quote" },
  { key: "find_work", label: "Find work for myself", hint: "Go to the side for individuals" },
  { key: "info", label: "Learn more about Sift", hint: "How it works and what it costs" },
];

// Enter goes, Shift+Enter is a new line. Nobody expected a one-line answer
// box to need a click (2026-10-06). Not mid-composition, so IME input works.
function enterGoes(e, go) {
  if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
  e.preventDefault();
  go();
}

const RESPONSES = {
  find_clients: {
    title: "Let clients find you",
    points: [
      "A storefront page for your company that clients can find and send requests from",
      "Listed in Sift's services catalog under what you do",
      "Answer a request with a quote, then contract and get paid in one place",
    ],
    cta: "Set up my company",
  },
  manage: {
    title: "Bring your people onto Sift",
    points: [
      "Contracts written for you and signed on a phone",
      "Timecards from their phones, approved in one click",
      "Payouts sent and 1099s handled",
      "Licenses and certificates tracked, with alerts before they expire",
    ],
    cta: "Set up my company",
  },
  /* "Learn more" gets the whole of what Sift does, not three lines (Jake,
     2026-10-06: the short version undersold it). Every line here is live. */
  info: {
    title: "How Sift works",
    lead: "One place for everything between finding people and paying them.",
    sections: [
      {
        icon: Search, heading: "Find people",
        points: [
          "Say what you need in a sentence and Sift writes the post",
          "Matched with people already on Sift, and again every time someone new joins",
          "Shared free to Google for Jobs, Indeed and other job sites",
          "Hire on contract, and keep anyone permanently with no fee, ever",
        ],
      },
      {
        icon: Users, heading: "Bring the people you already work with",
        points: [
          "Upload your team in one file; each person gets an invite and sets themselves up",
          "Keep them hidden from other companies if you choose",
        ],
      },
      {
        icon: FileSignature, heading: "Run the work",
        points: [
          "Contracts written for you and signed on a phone, with a signing certificate",
          "Timecards from their phones, approved in one click",
          "Licenses and certificates tracked, with alerts before they expire",
        ],
      },
      {
        icon: Wallet, heading: "Get paid and pay people",
        points: [
          "Payouts sent and 1099s handled",
          "Bill your clients through Sift; your margin comes to you and you release each person's pay",
          "A storefront page where clients find you, send requests, and get your quote",
        ],
      },
    ],
    cta: "Set up my company",
  },
};

const card = "relative w-full max-w-lg bg-surface border border-line rounded-2xl shadow-sift-sm p-7";
const backBtn = "absolute top-4 left-4 inline-flex items-center gap-1 min-h-[36px] px-1 text-xs text-secondary hover:text-ink";
const primary = "w-full min-h-[48px] rounded-pill bg-brand text-white font-semibold disabled:opacity-50";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-bg flex items-start sm:items-center justify-center px-4 pb-[40vh] sm:pb-16"
         style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 2rem)" }}>
      {children}
    </div>
  );
}

export default function CompanyAsk({ arm = "current", onSwitchAudience, onExit = null }) {
  const navigate = useNavigate();
  const box = useRef(null);
  const [text, setText] = useState("");
  const [stage, setStage] = useState("ask");        // ask | choose | respond | hire | account
  const [intent, setIntent] = useState(null);
  const [askId, setAskId] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [busy, setBusy] = useState(false);
  const [composed, setComposed] = useState(null);   // the current arm's draft, between preview and account
  const [clients, setClients] = useState("");       // their answer to "What kind of clients?"
  const [place, setPlace] = useState("");           // the answer was only a place

  useEffect(() => {
    trackEvent("company_flow_shown", { arm, door: "ask" });
  }, [arm]);

  const go = (to, said = text) => {
    setIntent(to);
    trackEvent("company_ask_routed", { arm, intent: to });
    // "Find clients" can mean a company or one person: ask before sending them either way.
    if (to === "find_clients") return setStage("clients");
    if (to === "find_work") {
      try { window.sessionStorage.setItem("sift_talent_need", said.slice(0, 300)); } catch { /* storage blocked */ }
      if (onSwitchAudience) return onSwitchAudience("talent");
      return navigate("/get-started?a=talent");
    }
    // A one-off job is posted like any other need until service requests are back.
    if (to === "hire" || to === "one_off") return setStage("hire");
    return setStage("respond");
  };

  const submit = async (said = text, source = "ask") => {
    const t = said.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const res = await fetch(apiUrl("/api/onboarding/ask/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ text: t, source, arm, anonymous_id: getAnonymousId() }),
        credentials: "omit",
      });
      const d = res.ok ? await res.json() : null;
      setAskId(d?.id || null);
      setPlace(d?.place || "");
      if (d?.clear && d.intent) return go(d.intent, t);
      if (d?.place) return setStage("place");
      setSuggested(d?.suggested || []);
      setStage("choose");
    } catch {
      // The API being down is not a reason to lose them: ask them to pick.
      setStage("choose");
    } finally {
      setBusy(false);
    }
  };

  // Typed into the landing page's search box: read it without asking again.
  useEffect(() => {
    let seeded = "";
    try {
      seeded = (window.sessionStorage.getItem("sift_hero_need") || "").trim();
      if (seeded) window.sessionStorage.removeItem("sift_hero_need");
    } catch { /* storage blocked */ }
    if (seeded) {
      setText(seeded);
      submit(seeded, "hero");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const record = (chosen, detail = "") => {
    if (!askId) return;
    fetch(apiUrl(`/api/onboarding/ask/${askId}/`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chosen, detail, anonymous_id: getAnonymousId() }),
      credentials: "omit",
    }).catch(() => {});
  };

  /* Answered "What kind of clients?": a company goes the company way, one
     person to the individual side with both answers carried into its box. */
  const clientsFor = (who) => {
    const kind = clients.trim();
    trackEvent("company_ask_clients", { arm, who });
    if (who === "company") {
      record("find_clients", kind);
      return setStage("respond");
    }
    record("find_work", kind);
    go("find_work", [text.trim(), kind].filter(Boolean).join(". "));
  };

  const [role, setRole] = useState("");
  const pick = (key) => {
    record(key);
    // They picked hire, but what they typed isn't a role ("hello"): ask
    // for the role rather than writing a post titled "Hello".
    if (key === "hire" || key === "one_off") {
      setIntent(key);
      trackEvent("company_ask_routed", { arm, intent: key, via: "choice" });
      return setStage("role");
    }
    go(key);
  };

  const ask = { text: text.trim(), intent };
  const backToAsk = () => { setStage("ask"); setComposed(null); };

  /* ---------------- hire: each arm's own last two screens ---------------- */
  if (stage === "hire" && arm === "new") {
    return <GetStartedNeedFirst audience="business" ask={ask} onBackToAsk={backToAsk} onSwitchAudience={onSwitchAudience} />;
  }
  if (stage === "hire" && !composed) {
    return (
      <CompanyNeedComposer seed={ask.text} autoDraft step={1} totalSteps={3}
        onBack={backToAsk}
        onDone={(draft) => { setComposed(draft || { skipped: true }); setStage("account"); }} />
    );
  }

  /* ---------------- the account ---------------- */
  if (stage === "account" || (stage === "hire" && composed)) {
    if (arm === "new") {
      return <GetStartedNeedFirst audience="business" ask={ask} startAt="account" onBackToAsk={() => setStage("respond")} onSwitchAudience={onSwitchAudience} />;
    }
    const hasDraft = composed && !composed.skipped;
    return (
      <GetStartedCompany
        onSwitchAudience={onSwitchAudience}
        intent={hasDraft ? "post" : "signup"}
        draft={hasDraft ? composed : null}
        ask={ask}
        onBack={() => {
          // One step back: to their post (redrafted from their words) or to the answer screen.
          if (hasDraft || ["hire", "one_off"].includes(intent)) { setComposed(null); setStage("hire"); } else setStage("respond");
        }}
        step={3}
        totalSteps={3}
      />
    );
  }

  /* ---------------- a short answer for manage / info ---------------- */
  if (stage === "respond") {
    const r = RESPONSES[intent] || RESPONSES.info;
    return (
      <Shell>
        <div className={card}>
          <button type="button" onClick={() => (intent === "find_clients" ? setStage("clients") : backToAsk())} className={backBtn}>
            <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
          </button>
          <div className="flex justify-center mb-5"><SiftMark size={44} /></div>
          <StepProgress step={2} total={3} />
          <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
            {r.title}
          </h1>
          {r.lead && <p className="mt-2 text-sm text-secondary">{r.lead}</p>}
          {r.points && (
            <ul className="mt-4 grid gap-2 p-0 list-none">
              {r.points.map((p) => (
                <li key={p} className="flex gap-2 text-sm text-secondary">
                  <Check size={16} strokeWidth={2.4} className="text-brand shrink-0 mt-0.5" aria-hidden="true" />{p}
                </li>
              ))}
            </ul>
          )}
          {r.sections && (
            <div className="mt-5 grid gap-5">
              {r.sections.map(({ icon, heading, points }) => { const Icon = icon; return (
                <section key={heading}>
                  <h2 className="flex items-center gap-2 m-0 text-sm font-semibold text-ink">
                    <Icon size={17} strokeWidth={2.2} className="text-brand shrink-0" aria-hidden="true" />{heading}
                  </h2>
                  <ul className="mt-2 grid gap-1.5 p-0 pl-[25px] list-none">
                    {points.map((p) => (
                      <li key={p} className="flex gap-2 text-sm text-secondary">
                        <Check size={14} strokeWidth={2.4} className="text-brand shrink-0 mt-[3px]" aria-hidden="true" />{p}
                      </li>
                    ))}
                  </ul>
                </section>
              ); })}
              <p className="m-0 rounded-xl bg-sunken px-4 py-3 text-sm text-secondary">
                Looking for work yourself? Sift costs you nothing.{" "}
                <button type="button" onClick={() => go("find_work", "")} className="text-brand font-medium underline">
                  Go to the side for individuals
                </button>
              </p>
            </div>
          )}
          <button type="button" onClick={() => { trackEvent("company_ask_respond_continue", { arm, intent }); setStage("account"); }}
            className={primary + " mt-6"}>
            {r.cta}
          </button>
          <a href="/contact" className="mt-3 block text-center text-sm text-secondary underline hover:text-ink">
            Talk to us first
          </a>
        </div>
      </Shell>
    );
  }

  /* ---------------- find clients: what kind, and who does the work? ---------------- */
  if (stage === "clients") {
    const ready = clients.trim().length >= 3;
    return (
      <Shell>
        <div className={card}>
          <button type="button" onClick={backToAsk} className={backBtn}>
            <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
          </button>
          <div className="flex justify-center mb-5"><SiftMark size={44} /></div>
          <StepProgress step={1} total={3} />
          <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
            What kind of clients are you looking for?
          </h1>
          <textarea value={clients} onChange={(e) => setClients(e.target.value)} rows={3} maxLength={500}
            aria-label="What kind of clients are you looking for?"
            className="mt-4 w-full rounded-xl border border-line-control bg-surface px-3 py-2.5 text-ink resize-y focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand" />
          <p className="mt-5 mb-2 text-sm font-semibold text-ink">Who will do the work?</p>
          <div className="grid gap-2">
            <button type="button" disabled={!ready} onClick={() => clientsFor("company")}
              className="text-left rounded-xl border border-line px-4 py-3 hover:border-brand hover:bg-brand-tint/40 disabled:opacity-50 disabled:pointer-events-none">
              <span className="block font-semibold text-ink text-sm">My company</span>
              <span className="block text-xs text-secondary mt-0.5">A business, agency or crew</span>
            </button>
            <button type="button" disabled={!ready} onClick={() => clientsFor("me")}
              className="text-left rounded-xl border border-line px-4 py-3 hover:border-brand hover:bg-brand-tint/40 disabled:opacity-50 disabled:pointer-events-none">
              <span className="block font-semibold text-ink text-sm">Just me</span>
              <span className="block text-xs text-secondary mt-0.5">I work for myself</span>
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  /* ---------------- only a place: work there, or hiring there? ---------------- */
  if (stage === "place") {
    return (
      <Shell>
        <div className={card}>
          <button type="button" onClick={backToAsk} className={backBtn}>
            <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
          </button>
          <div className="flex justify-center mb-5"><SiftMark size={44} /></div>
          <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
            Are you looking for work or looking to hire in {place}?
          </h1>
          <div className="mt-4 grid gap-2">
            <button type="button" onClick={() => { record("find_work"); go("find_work", ""); }}
              className="text-left rounded-xl border border-line px-4 py-3 hover:border-brand hover:bg-brand-tint/40">
              <span className="block font-semibold text-ink text-sm">Looking for work in {place}</span>
            </button>
            <button type="button" onClick={() => pick("hire")}
              className="text-left rounded-xl border border-line px-4 py-3 hover:border-brand hover:bg-brand-tint/40">
              <span className="block font-semibold text-ink text-sm">Hiring in {place}</span>
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  /* ---------------- picked hire from the choices: which role? ---------------- */
  if (stage === "role") {
    return (
      <Shell>
        <div className={card}>
          <button type="button" onClick={() => setStage(place ? "place" : "choose")} className={backBtn}>
            <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
          </button>
          <div className="flex justify-center mb-5"><SiftMark size={44} /></div>
          <StepProgress step={1} total={3} />
          <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
            Who are you looking for?
          </h1>
          <textarea value={role} onChange={(e) => setRole(e.target.value)} rows={3} maxLength={500}
            onKeyDown={(e) => enterGoes(e, () => { if (role.trim().length >= 3) { setText(role.trim()); setStage("hire"); } })}
            aria-label="Who are you looking for?"
            className="mt-4 w-full rounded-xl border border-line-control bg-surface px-3 py-2.5 text-ink resize-y focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand" />
          <button type="button" disabled={role.trim().length < 3}
            onClick={() => { setText(place ? `${role.trim()} in ${place}` : role.trim()); setStage("hire"); }}
            className={primary + " mt-4"}>
            Continue
          </button>
        </div>
      </Shell>
    );
  }

  /* ---------------- couldn't read it: pick the closest ---------------- */
  if (stage === "choose") {
    const order = [...suggested, ...CHOICES.map((c) => c.key)].filter((k, i, a) => a.indexOf(k) === i);
    return (
      <Shell>
        <div className={card}>
          <button type="button" onClick={backToAsk} className={backBtn}>
            <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
          </button>
          <div className="flex justify-center mb-5"><SiftMark size={44} /></div>
          <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
            Which is closest?
          </h1>
          <div className="mt-4 grid gap-2">
            {order.map((k) => {
              const c = CHOICES.find((x) => x.key === k);
              return (
                <button key={k} type="button" onClick={() => pick(k)}
                  className="text-left rounded-xl border border-line px-4 py-3 hover:border-brand hover:bg-brand-tint/40">
                  <span className="block font-semibold text-ink text-sm">{c.label}</span>
                  <span className="block text-xs text-secondary mt-0.5">{c.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Shell>
    );
  }

  /* ---------------- the question ---------------- */
  return (
    <Shell>
      <div className={card}>
        <button type="button" onClick={() => (onExit ? onExit() : navigate("/"))} className={backBtn}>
          <ArrowLeft size={13} strokeWidth={2.2} aria-hidden="true" /> Back
        </button>
        <div className="flex justify-center mb-5"><SiftMark size={44} /></div>
        <StepProgress step={1} total={3} />
        <h1 className="font-display" style={{ fontSize: "clamp(1.375rem,4.6vw,1.8125rem)", lineHeight: 1.15, margin: 0, color: "var(--ink)" }}>
          What can we help you with?
        </h1>
        <textarea
          ref={box}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => enterGoes(e, () => submit())}
          rows={5}
          maxLength={500}
          aria-label="What can we help you with?"
          className="mt-4 w-full rounded-xl border border-line-control bg-surface px-3 py-2.5 text-ink resize-y focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
        />
        <button type="button" onClick={() => submit()} disabled={!text.trim() || busy} className={primary + " mt-4"}>
          {busy ? "One moment…" : "Continue"}
        </button>
      </div>
    </Shell>
  );
}
